// Client de la API pública de retail-promotions-business. Sin base URL no hace ninguna petición:
// caer al base URL por defecto le pegaría a otro API.
import { apiFetch } from '@/api/client';
import { parseErrorResponse } from '@/api/errors';
import type {
    ChangeDiscountCodeStatusRequest,
    ChangeDiscountCodeStatusResponse,
    ChangeSalePromotionStatusQuery,
    ChangeSalePromotionStatusRequest,
    ChangeSalePromotionStatusResponse,
    CreateDiscountCodeRequest,
    CreateDiscountCodeResponse,
    CreateSalePromotionQuery,
    CreateSalePromotionRequest,
    CreateSalePromotionResponse,
    GetSalePromotionResponse,
    ListDiscountCodesQuery,
    ListDiscountCodesResponse,
    ListSalePromotionLogQuery,
    ListSalePromotionLogResponse,
    ListSalePromotionSegmentsResponse,
    ListSalePromotionsQuery,
    ListSalePromotionsResponse,
    UpdateSalePromotionQuery,
    UpdateSalePromotionRequest,
    UpdateSalePromotionResponse,
} from './promotionsContract';

export class PromotionsServiceNotConfiguredError extends Error {
    constructor() {
        super('El servicio de promociones no está configurado en este ambiente.');
        this.name = 'PromotionsServiceNotConfiguredError';
    }
}

/** Base URL leída en cada llamada; `null` si falta o viene vacía. */
function configuredBaseUrl(): string | null {
    const value = import.meta.env.VITE_API_RETAIL_PROMOTIONS_BASE_URL as string | undefined;
    return value?.trim() ? value : null;
}

export function isPromotionsConfigured(): boolean {
    return configuredBaseUrl() !== null;
}

function promotionsFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    const baseUrl = configuredBaseUrl();
    if (!baseUrl) return Promise.reject(new PromotionsServiceNotConfiguredError());
    return apiFetch<T>(path, { ...init, baseUrl });
}

/** `apiFetch` solo lee JSON; el CSV llega como `text/csv` y se lee como blob. */
async function promotionsFetchBlob(path: string): Promise<Blob> {
    const baseUrl = configuredBaseUrl();
    if (!baseUrl) throw new PromotionsServiceNotConfiguredError();
    const response = await fetch(`${baseUrl}${path}`, { credentials: 'include' });
    if (!response.ok) throw await parseErrorResponse(response);
    return response.blob();
}

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) query.set(key, String(value));
    }
    const text = query.toString();
    return text ? `${path}?${text}` : path;
}

const LOG_PATH = '/public/sale-promotion-log';
const PROMOTIONS_PATH = '/public/sale-promotions';
const DISCOUNT_CODES_PATH = '/public/discount-codes';
const SEGMENTS_PATH = '/public/segments';

function promotionPath(promotionId: string): string {
    return `${PROMOTIONS_PATH}/${encodeURIComponent(promotionId)}`;
}

export const promotionsClient = {
    listSalePromotions: (query: ListSalePromotionsQuery): Promise<ListSalePromotionsResponse> =>
        promotionsFetch(
            withQuery(PROMOTIONS_PATH, {
                status: query.status,
                kind: query.kind,
                channel: query.channel,
                limit: query.limit,
                nextToken: query.nextToken,
                retailerId: query.retailerId,
            }),
        ),

    changeSalePromotionStatus: (
        promotionId: string,
        request: ChangeSalePromotionStatusRequest,
        { retailerId }: ChangeSalePromotionStatusQuery = {},
    ): Promise<ChangeSalePromotionStatusResponse> =>
        promotionsFetch(withQuery(`${promotionPath(promotionId)}/status`, { retailerId }), {
            method: 'PUT',
            body: JSON.stringify(request),
        }),

    createSalePromotion: (
        request: CreateSalePromotionRequest,
        { retailerId }: CreateSalePromotionQuery = {},
    ): Promise<CreateSalePromotionResponse> =>
        promotionsFetch(withQuery(PROMOTIONS_PATH, { retailerId }), {
            method: 'POST',
            body: JSON.stringify(request),
        }),

    getSalePromotion: (promotionId: string): Promise<GetSalePromotionResponse> =>
        promotionsFetch(promotionPath(promotionId)),

    updateSalePromotion: (
        promotionId: string,
        request: UpdateSalePromotionRequest,
        { retailerId }: UpdateSalePromotionQuery = {},
    ): Promise<UpdateSalePromotionResponse> =>
        promotionsFetch(withQuery(promotionPath(promotionId), { retailerId }), {
            method: 'PATCH',
            body: JSON.stringify(request),
        }),

    listSalePromotionLog: (
        query: ListSalePromotionLogQuery,
    ): Promise<ListSalePromotionLogResponse> =>
        promotionsFetch(
            withQuery(LOG_PATH, {
                promotionId: query.promotionId,
                limit: query.limit,
                nextToken: query.nextToken,
                retailerId: query.retailerId,
            }),
        ),

    exportSalePromotionLogCsv: (
        query: Pick<ListSalePromotionLogQuery, 'promotionId' | 'retailerId'>,
    ): Promise<Blob> =>
        promotionsFetchBlob(
            withQuery(LOG_PATH, {
                promotionId: query.promotionId,
                format: 'CSV',
                retailerId: query.retailerId,
            }),
        ),

    listSalePromotionSegments: (): Promise<ListSalePromotionSegmentsResponse> =>
        promotionsFetch(SEGMENTS_PATH),

    listDiscountCodes: (query: ListDiscountCodesQuery): Promise<ListDiscountCodesResponse> =>
        promotionsFetch(
            withQuery(DISCOUNT_CODES_PATH, {
                origin: query.origin,
                status: query.status,
                limit: query.limit,
                nextToken: query.nextToken,
                retailerId: query.retailerId,
            }),
        ),

    createDiscountCode: (
        request: CreateDiscountCodeRequest,
        { retailerId }: { retailerId?: string } = {},
    ): Promise<CreateDiscountCodeResponse> =>
        promotionsFetch(withQuery(DISCOUNT_CODES_PATH, { retailerId }), {
            method: 'POST',
            body: JSON.stringify(request),
        }),

    changeDiscountCodeStatus: (
        codeId: string,
        request: ChangeDiscountCodeStatusRequest,
        { retailerId }: { retailerId?: string } = {},
    ): Promise<ChangeDiscountCodeStatusResponse> =>
        promotionsFetch(
            withQuery(`${DISCOUNT_CODES_PATH}/${encodeURIComponent(codeId)}/status`, {
                retailerId,
            }),
            { method: 'PUT', body: JSON.stringify(request) },
        ),
};
